// =============================================================================
// CALENDAR STORE (Zustand)
// =============================================================================

import { create } from 'zustand';
import { api } from '../api/client.js';
import type {
  CalendarEvent,
  EventSummary,
  RsvpStatus,
} from '@chlann/shared';

interface CalendarState {
  events: EventSummary[];
  selectedEvent: CalendarEvent | null;
  selectedDate: Date;
  currentMonth: Date;
  isLoading: boolean;
  isEventModalOpen: boolean;
  editingEventId: string | null;
  error: string | null;

  loadEvents: (familyId: string, startDate: string, endDate: string) => Promise<void>;
  loadEventDetail: (eventId: string) => Promise<void>;
  createEvent: (input: {
    familyId: string;
    title: string;
    description?: string;
    location?: string;
    color?: string;
    isAllDay?: boolean;
    startAt: string;
    endAt: string;
    recurrence?: string;
    recurrenceEndAt?: string;
    reminderMinutes?: number;
  }) => Promise<string | null>;
  updateEvent: (eventId: string, updates: Record<string, unknown>) => Promise<boolean>;
  deleteEvent: (eventId: string) => Promise<boolean>;
  setRsvp: (eventId: string, status: RsvpStatus) => Promise<boolean>;

  setSelectedDate: (date: Date) => void;
  setCurrentMonth: (date: Date) => void;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToToday: () => void;

  openCreateModal: (date?: Date) => void;
  openEditModal: (eventId: string) => void;
  closeEventModal: () => void;

  handleEventCreated: (data: { event: CalendarEvent }) => void;
  handleEventUpdated: (data: { event: CalendarEvent }) => void;
  handleEventDeleted: (data: { eventId: string }) => void;
  handleRsvpUpdated: (data: {
    eventId: string;
    userId: string;
    rsvpStatus: RsvpStatus;
  }) => void;

  clearError: () => void;
  reset: () => void;
}

function eventToSummary(event: CalendarEvent): EventSummary {
  return {
    id: event.id,
    title: event.title,
    color: event.color,
    isAllDay: event.isAllDay,
    startAt: event.startAt,
    endAt: event.endAt,
    rsvpStatus: null,
    attendeeCount: event.attendees.length,
  };
}

export const useCalendarStore = create<CalendarState>()((set, get) => ({
  events: [],
  selectedEvent: null,
  selectedDate: new Date(),
  currentMonth: new Date(),
  isLoading: false,
  isEventModalOpen: false,
  editingEventId: null,
  error: null,

  loadEvents: async (familyId, startDate, endDate) => {
    set({ isLoading: true, error: null });
    const result = await api.getEvents({ familyId, startDate, endDate });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
    } else if (result.data) {
      set({
        isLoading: false,
        events: result.data.events as unknown as EventSummary[],
      });
    }
  },

  loadEventDetail: async (eventId) => {
    const result = await api.getEventDetail(eventId);
    if (result.data) {
      set({ selectedEvent: result.data.event as unknown as CalendarEvent });
    }
  },

  createEvent: async (input) => {
    set({ isLoading: true, error: null });
    const result = await api.createEvent(input);

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return null;
    }

    // Add event to local state for instant feedback (socket will deduplicate)
    if (result.data?.event) {
      const created = result.data.event as unknown as CalendarEvent;
      set((state) => ({
        isLoading: false,
        isEventModalOpen: false,
        editingEventId: null,
        events: state.events.some((e) => e.id === created.id)
          ? state.events
          : [...state.events, eventToSummary(created)],
      }));
    } else {
      set({ isLoading: false, isEventModalOpen: false, editingEventId: null });
    }
    return result.data?.event.id ?? null;
  },

  updateEvent: async (eventId, updates) => {
    const result = await api.updateEvent(eventId, updates);
    if (result.error) {
      set({ error: result.error.message });
      return false;
    }
    // Update local state for instant feedback (cast to match shared types)
    if (result.data?.event) {
      const updated = result.data.event as unknown as CalendarEvent;
      set((state) => ({
        isEventModalOpen: false,
        editingEventId: null,
        events: state.events.map((e) =>
          e.id === eventId ? eventToSummary(updated) : e
        ),
        selectedEvent:
          state.selectedEvent?.id === eventId ? updated : state.selectedEvent,
      }));
    } else {
      set({ isEventModalOpen: false, editingEventId: null });
    }
    return true;
  },

  deleteEvent: async (eventId) => {
    const result = await api.deleteEvent(eventId);
    if (result.error) {
      set({ error: result.error.message });
      return false;
    }
    set((state) => ({
      events: state.events.filter((e) => e.id !== eventId),
      selectedEvent:
        state.selectedEvent?.id === eventId ? null : state.selectedEvent,
    }));
    return true;
  },

  setRsvp: async (eventId, status) => {
    const result = await api.rsvpEvent(eventId, status);
    if (result.error) {
      set({ error: result.error.message });
      return false;
    }
    // Update local event's RSVP
    set((state) => ({
      events: state.events.map((e) =>
        e.id === eventId ? { ...e, rsvpStatus: status } : e
      ),
    }));
    return true;
  },

  setSelectedDate: (date) => { set({ selectedDate: date }); },
  setCurrentMonth: (date) => { set({ currentMonth: date }); },

  goToPreviousMonth: () => {
    const current = get().currentMonth;
    set({
      currentMonth: new Date(
        current.getFullYear(),
        current.getMonth() - 1,
        1
      ),
    });
  },

  goToNextMonth: () => {
    const current = get().currentMonth;
    set({
      currentMonth: new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        1
      ),
    });
  },

  goToToday: () => {
    const today = new Date();
    set({ selectedDate: today, currentMonth: today });
  },

  openCreateModal: (date) =>
    { set({
      isEventModalOpen: true,
      editingEventId: null,
      selectedDate: date || get().selectedDate,
      selectedEvent: null,
    }); },

  openEditModal: (eventId) =>
    { set({
      isEventModalOpen: true,
      editingEventId: eventId,
    }); },

  closeEventModal: () =>
    { set({
      isEventModalOpen: false,
      editingEventId: null,
    }); },

  handleEventCreated: (data) => {
    set((state) => ({
      events: [...state.events, eventToSummary(data.event)],
    }));
  },

  handleEventUpdated: (data) => {
    set((state) => ({
      events: state.events.map((e) =>
        e.id === data.event.id ? eventToSummary(data.event) : e
      ),
      selectedEvent:
        state.selectedEvent?.id === data.event.id
          ? data.event
          : state.selectedEvent,
    }));
  },

  handleEventDeleted: (data) => {
    set((state) => ({
      events: state.events.filter((e) => e.id !== data.eventId),
      selectedEvent:
        state.selectedEvent?.id === data.eventId
          ? null
          : state.selectedEvent,
    }));
  },

  handleRsvpUpdated: (data) => {
    set((state) => ({
      events: state.events.map((e) =>
        e.id === data.eventId
          ? { ...e, rsvpStatus: data.rsvpStatus }
          : e
      ),
    }));
  },

  clearError: () => { set({ error: null }); },

  reset: () => { set({
    events: [],
    selectedEvent: null,
    selectedDate: new Date(),
    currentMonth: new Date(),
    isLoading: false,
    isEventModalOpen: false,
    editingEventId: null,
    error: null,
  }); },
}));
